import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import { ErrorMessage } from "@atlaskit/form";
import AddIcon from "@atlaskit/icon/core/add";
import CloseIcon from "@atlaskit/icon/core/close";
import { Box, Flex, Stack, xcss } from "@atlaskit/primitives";
import Textfield from "@atlaskit/textfield";
import { token } from "@atlaskit/tokens";
import Tooltip from "@atlaskit/tooltip";

import {
    ANALYTICS_PREFIX,
    MAX_COLUMNS_LEN,
    MIN_COLUMNS_LEN,
    MAX_INPUT_LENGTH
} from "../../../common/constants.tsx";
import { useWizardConfiguration } from "../../../controllers";

import messages from "./messages.tsx";

type ColumnNameProps = {
    columnNames: string[];
    setColumnNames: React.Dispatch<React.SetStateAction<string[]>>;
};

export const updateColumnNames = ({
    newValue,
    columnKey,
    columnNames,
    setColumnNames
}: {
    newValue: string;
    columnKey: number;
} & ColumnNameProps) => {
    const newColumnNames = [...columnNames];
    newColumnNames[columnKey] = newValue;
    setColumnNames(newColumnNames);
};

export const addNewColumn = ({
    columnNames,
    setColumnNames
}: ColumnNameProps) => {
    const newColumnNames = [...columnNames];
    newColumnNames.splice(columnNames.length - 1, 0, "");
    setColumnNames(newColumnNames);
};

export const adjustColumnNameWhitespaces = (columnName: string) => {
    return columnName.trim().replace(/\s+/g, " ");
};

export const getEmptyInputIndexes = (columnNames: string[]) => {
    const emptyInputIndexes: number[] = [];
    columnNames.forEach(
        (columnName, i) => columnName === "" && emptyInputIndexes.push(i)
    );
    return emptyInputIndexes;
};

export const getDupeIndexes = (columnNames: string[]) => {
    const dupeIndexes: number[] = [];
    const indexMap: Map<string, number> = new Map();
    columnNames.forEach((columnName) => {
        indexMap.set(columnName, (indexMap.get(columnName) || 0) + 1);
    });
    columnNames.forEach((columnName, i) => {
        const columnNameCount = indexMap.get(columnName);
        if (
            columnName !== "" &&
            columnNameCount !== undefined &&
            columnNameCount > 1
        ) {
            dupeIndexes.push(i);
        }
    });
    return dupeIndexes;
};

export const getExceededInputLengthIndexes = (
    columnNames: string[],
    dupeIndexes: number[]
) => {
    const exceededIndexes: number[] = [];
    columnNames.forEach(
        (columnName, i) =>
            columnName.length > MAX_INPUT_LENGTH &&
            !dupeIndexes.includes(i) &&
            exceededIndexes.push(i)
    );
    return exceededIndexes;
};

export const onClickFinishButton = ({
    onClickNext,
    columnNames,
    setEmptyInputIndexes,
    setDupeIndexes,
    setExceededInputLengthIndexes
}: {
    onClickNext: ({ columnNames }: { columnNames: string[] }) => void;
    columnNames: string[];
    setEmptyInputIndexes: React.Dispatch<React.SetStateAction<number[]>>;
    setDupeIndexes: React.Dispatch<React.SetStateAction<number[]>>;
    setExceededInputLengthIndexes: React.Dispatch<
        React.SetStateAction<number[]>
    >;
}) => {
    const newColumnNames: string[] = columnNames.map((columnName) =>
        adjustColumnNameWhitespaces(columnName)
    );
    const emptyInputIndexes: number[] = getEmptyInputIndexes(newColumnNames);
    const dupeIndexes: number[] = getDupeIndexes(newColumnNames);
    const exceededIndexes: number[] = getExceededInputLengthIndexes(
        newColumnNames,
        dupeIndexes
    );
    if (emptyInputIndexes.length > 0) {
        setEmptyInputIndexes(emptyInputIndexes);
    } else {
        setEmptyInputIndexes([]);
    }
    if (dupeIndexes.length > 0) {
        setDupeIndexes(dupeIndexes);
    } else {
        setDupeIndexes([]);
    }
    if (exceededIndexes.length > 0) {
        setExceededInputLengthIndexes(exceededIndexes);
    } else {
        setExceededInputLengthIndexes([]);
    }
    if (
        emptyInputIndexes.length > 0 ||
        dupeIndexes.length > 0 ||
        exceededIndexes.length > 0
    ) {
        return;
    }
    onClickNext({ columnNames: newColumnNames });
};

export const DeleteColumnButton = ({
    columnNames,
    setColumnNames,
    index,
    emptyInputIndexes,
    setEmptyInputIndexes,
    setDupeIndexes,
    setExceededInputLengthIndexes
}: {
    index: number;
    emptyInputIndexes: number[];
    setEmptyInputIndexes: React.Dispatch<React.SetStateAction<number[]>>;
    setDupeIndexes: React.Dispatch<React.SetStateAction<number[]>>;
    setExceededInputLengthIndexes: React.Dispatch<
        React.SetStateAction<number[]>
    >;
} & ColumnNameProps) => {
    const { formatMessage } = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { teamType } = useWizardConfiguration();

    const isMinColumnLen = columnNames.length <= MIN_COLUMNS_LEN;
    const buttonLabel = formatMessage(messages.deleteColumnButtonLabel, {
        columnNumber: index + 1
    });

    const handleDelete = () => {
        if (isMinColumnLen) {
            return;
        }
        const newColumnNames = [...columnNames];
        newColumnNames.splice(index, 1);
        const updatedEmptyInputIndexes = emptyInputIndexes.filter(
            (emptyIndex) => newColumnNames[emptyIndex] === ""
        );
        setEmptyInputIndexes(updatedEmptyInputIndexes);
        const newDupeIndexes = getDupeIndexes(newColumnNames);
        setDupeIndexes(newDupeIndexes);
        setExceededInputLengthIndexes(
            getExceededInputLengthIndexes(newColumnNames, newDupeIndexes)
        );
        setColumnNames(newColumnNames);

        createAnalyticsEvent({
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: `${ANALYTICS_PREFIX}DeleteBoardColumn`,
            eventType: "ui",
            attributes: { teamType }
        }).fire();
    };
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleDelete();
        }
    };

    return (
        <Tooltip
            content={
                columnNames.length === MIN_COLUMNS_LEN ? (
                    <FormattedMessage {...messages.deleteTooltip} />
                ) : (
                    ""
                )
            }
            position="right-end"
        >
            <Button
                isDisabled={isMinColumnLen}
                css={deleteButtonStyles}
                aria-label={buttonLabel}
                onClick={handleDelete}
                onKeyDown={handleKeyDown}
            >
                <CloseIcon label="" />
            </Button>
        </Tooltip>
    );
};

const ColumnInputErrorMessage = ({
    hasEmptyInput,
    hasDupeInput,
    hasExceededInput
}: {
    hasEmptyInput: boolean;
    hasDupeInput: boolean;
    hasExceededInput: boolean;
}) => {
    const getErrorMessage = () => {
        switch (true) {
            case hasDupeInput:
                return messages.duplicateStatusErrorMessage;
            case hasExceededInput:
                return messages.inputLengthErrorMessage;
            case hasEmptyInput:
                return messages.emptyInputErrorMessage;
            default:
                return null;
        }
    };
    const errorMessage = getErrorMessage();
    return errorMessage ? (
        <Stack space="space.050" xcss={bottomPadding}>
            <ErrorMessage>
                <FormattedMessage {...errorMessage} />
            </ErrorMessage>
        </Stack>
    ) : (
        <div css={verticalSpacing} />
    );
};

export const ColumnInputs = ({
    columnNames,
    setColumnNames,
    emptyInputIndexes,
    setEmptyInputIndexes,
    dupeIndexes,
    setDupeIndexes,
    exceededInputLengthIndexes,
    setExceededInputLengthIndexes
}: ColumnNameProps & {
    emptyInputIndexes: number[];
    dupeIndexes: number[];
    exceededInputLengthIndexes: number[];
    setEmptyInputIndexes: React.Dispatch<React.SetStateAction<number[]>>;
    setDupeIndexes: React.Dispatch<React.SetStateAction<number[]>>;
    setExceededInputLengthIndexes: React.Dispatch<
        React.SetStateAction<number[]>
    >;
}) => {
    const { formatMessage } = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { teamType } = useWizardConfiguration();
    return (
        <Box xcss={columnInputWrapperStyles}>
            {columnNames.map((columnName, index) => {
                return (
                    <div key={index} css={inputWidthStyle}>
                        <Flex direction="row">
                            <Textfield
                                width="100%"
                                value={columnName}
                                placeholder={formatMessage(
                                    messages.statusInputPlaceholder
                                )}
                                isInvalid={
                                    emptyInputIndexes.includes(index) ||
                                    dupeIndexes.includes(index) ||
                                    exceededInputLengthIndexes.includes(index)
                                }
                                onChange={(e) => {
                                    updateColumnNames({
                                        newValue: e.currentTarget.value,
                                        columnKey: index,
                                        columnNames,
                                        setColumnNames
                                    });
                                }}
                            />
                            <div css={horizontalSpacing} />
                            <DeleteColumnButton
                                columnNames={columnNames}
                                setColumnNames={setColumnNames}
                                index={index}
                                emptyInputIndexes={emptyInputIndexes}
                                setEmptyInputIndexes={setEmptyInputIndexes}
                                setDupeIndexes={setDupeIndexes}
                                setExceededInputLengthIndexes={
                                    setExceededInputLengthIndexes
                                }
                            />
                        </Flex>
                        <ColumnInputErrorMessage
                            hasEmptyInput={emptyInputIndexes.includes(index)}
                            hasDupeInput={dupeIndexes.includes(index)}
                            hasExceededInput={exceededInputLengthIndexes.includes(
                                index
                            )}
                        />
                    </div>
                );
            })}
            {columnNames.length === MAX_COLUMNS_LEN ? (
                <Box xcss={maxColumnsTextStyles}>
                    <FormattedMessage {...messages.maxColumnsText} />
                </Box>
            ) : (
                <Box xcss={addColumnButtonPadding}>
                    <Button
                        appearance="subtle"
                        iconBefore={<AddIcon label={""} />}
                        onClick={() => {
                            const secondLastIndex = columnNames.length - 1;
                            const lastIndex = columnNames.length;
                            if (emptyInputIndexes.includes(secondLastIndex)) {
                                const newEmptyInputIndexes =
                                    emptyInputIndexes.filter(
                                        (emptyIndex) =>
                                            emptyIndex !== secondLastIndex
                                    );
                                newEmptyInputIndexes.push(lastIndex);
                                setEmptyInputIndexes(newEmptyInputIndexes);
                            }
                            if (dupeIndexes.includes(secondLastIndex)) {
                                const newDupeIndexes = dupeIndexes.filter(
                                    (dupeIndex) => dupeIndex !== secondLastIndex
                                );
                                newDupeIndexes.push(lastIndex);
                                setDupeIndexes(newDupeIndexes);
                            }
                            if (
                                exceededInputLengthIndexes.includes(
                                    secondLastIndex
                                )
                            ) {
                                const newExceededInputLengthIndexes =
                                    exceededInputLengthIndexes.filter(
                                        (exceededInputLengthIndex) =>
                                            exceededInputLengthIndex !==
                                            secondLastIndex
                                    );
                                newExceededInputLengthIndexes.push(lastIndex);
                                setExceededInputLengthIndexes(
                                    newExceededInputLengthIndexes
                                );
                            }
                            addNewColumn({
                                columnNames,
                                setColumnNames
                            });
                            createAnalyticsEvent({
                                action: "clicked",
                                actionSubject: "button",
                                actionSubjectId: `${ANALYTICS_PREFIX}AddBoardColumn`,
                                eventType: "ui",
                                attributes: {
                                    teamType
                                }
                            }).fire();
                        }}
                        isDisabled={columnNames.length >= MAX_COLUMNS_LEN}
                    >
                        <FormattedMessage {...messages.addStatusButton} />
                    </Button>
                </Box>
            )}
        </Box>
    );
};

const maxColumnsTextStyles = xcss({ color: "color.text.subtlest" });
const addColumnButtonPadding = xcss({
    paddingInlineStart: "space.050",
    paddingBlockEnd: "space.050"
});
const inputWidthStyle = css({ width: "100%" });
const horizontalSpacing = css({ width: token("space.150") });
const verticalSpacing = css({ height: token("space.100") });
const bottomPadding = xcss({ paddingBlockEnd: "space.050" });
const deleteButtonStyles = css({
    "minWidth": "46px",
    "height": "48px",
    "backgroundColor": "color.background.neutral.subtle",
    "border": `1px solid ${token("color.border")}`,
    "borderRadius": "border.radius.200",
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center",
    ":hover": {
        backgroundColor: "color.background.input.hovered"
    }
});
const columnInputWrapperStyles = xcss({
    maxWidth: "460px",
    minWidth: "250px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    paddingBlockStart: "space.150"
});
